import React, {useMemo, useRef, useState} from "react";
import FlexBox from "view/components/Layout/FlexBox";
import Button from "@material-ui/core/Button";
import {Chip, MenuItem, TextField, Tooltip} from "@material-ui/core";
import useOutsideClick from "view/components/RichTextEditor/hooks/useOutsideClick";
import ClearIcon from "assets/ClearIcon";
import {Autocomplete} from "@mui/material";
import caseInsensitiveSort from "tools/common/caseInsensitiveSort";
import AddIcon from "@material-ui/icons/Add";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import clsx from "clsx";
import {useTheme} from "@material-ui/core/styles";
import {MAX_TAG_LEN, MIN_TAG_LEN} from "./shared";
import useStyles from "./styles";
import {WarningMaxCount} from "./TagsModal/shared";

// Minimal tag shape so this chip-list stays decoupled from wire types.
export type TagDefinition = {id: string; name: string};

type TaskTagsProps = {
  tagDefinitions?: TagDefinition[];
  tags?: string[];
  addTag?: (
    text: string,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => Promise<void>;
  removeTag?: (id: string) => Promise<void> | void;
  maxWidth?: string;
};

const enterKeyCode = "Enter";

function Tags(props: TaskTagsProps) {
  const classes = useStyles();
  const {tagDefinitions, tags, removeTag, maxWidth} = props;

  const autocompleteOptions = useMemo(() => {
    const filteredTags =
      tagDefinitions?.filter((tag) => !tags?.includes(tag.id)) || [];

    return caseInsensitiveSort(filteredTags, "name").map((tag) => tag.name);
  }, [tagDefinitions, tags]);

  if (!tagDefinitions?.length || !tags?.length) {
    return (
      <FlexBox alignCenter>
        <AddNewTag {...props} autocompleteOptions={autocompleteOptions} />
      </FlexBox>
    );
  }

  const tagsWithText = tags.reduce<{id: string; name: string}[]>(
    (acc, tagId) => {
      const tag = tagDefinitions.find((taskTag) => taskTag.id === tagId);
      if (!tag) {
        return acc;
      }
      return [...acc, {id: tag.id, name: tag.name}];
    },
    []
  );

  return (
    <FlexBox maxWidth={maxWidth} alignCenter gap={1} className={classes.root}>
      {tagsWithText.map(({name, id}) => {
        return (
          <Chip
            className={classes.chip}
            deleteIcon={
              <Tooltip title="Remove">
                <ClearIcon className={classes.chipDeleteIcon} />
              </Tooltip>
            }
            label={name}
            key={id}
            onDelete={removeTag ? () => removeTag(id) : undefined}
          />
        );
      })}
      <AddNewTag {...props} autocompleteOptions={autocompleteOptions} />
    </FlexBox>
  );
}

function AddNewTag({
  addTag,
  tags,
  tagDefinitions,
  autocompleteOptions = [],
}: Omit<TaskTagsProps, "maxWidth"> & {autocompleteOptions?: string[]}) {
  const classes = useStyles();
  const theme = useTheme();
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  const onClickOutside = () => {
    if (!inputRef.current || !paperRef?.current) {
      return;
    }
    setIsAddingMode(false);
  };

  useOutsideClick([inputRef, paperRef], onClickOutside);

  if (!addTag) {
    return null;
  }

  if (!isAddingMode) {
    return (
      <Button
        color="primary"
        size="small"
        variant="text"
        onClick={() => setIsAddingMode(true)}
      >
        + Tag
      </Button>
    );
  }

  const isDuplicateTag = tagDefinitions?.some(
    (taskTag) => taskTag.name.toLowerCase() === text.trim().toLowerCase()
  );
  const isSubmittionDisabled =
    text.trim().length < MIN_TAG_LEN ||
    text.trim().length > MAX_TAG_LEN ||
    isDuplicateTag;

  const onSave = async (option?: string) => {
    const tagText = option ?? text;

    const alreadyCreatedTagId = tagDefinitions?.find(
      (taskTag) => taskTag.name === tagText.trim()
    )?.id;
    if (!alreadyCreatedTagId || !tags?.includes(alreadyCreatedTagId)) {
      await addTag(tagText.trim());
    }
    setText("");
    setIsAddingMode(false);
  };

  const handleEnterKey = () => {
    const exactMatchOption = autocompleteOptions.find(
      (option) => option.toLowerCase() === text.trim().toLowerCase()
    );

    if (exactMatchOption) {
      onSave(exactMatchOption);
    } else if (!isSubmittionDisabled) {
      onSave();
    }
  };

  return (
    <FlexBox alignCenter gap={1} className={classes.root}>
      <Autocomplete
        freeSolo
        open
        disableClearable
        ref={inputRef}
        options={autocompleteOptions || []}
        inputValue={text}
        onInputChange={(_, newValue) => {
          if (newValue.length > MAX_TAG_LEN) {
            return;
          }
          setText(newValue);
        }}
        PaperComponent={({children}) => (
          <div className={classes.paperComponent} ref={paperRef}>
            {children}
            <MenuItem className={classes.menuItem}>
              <FlexBox column gap={0.5}>
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  disabled={isSubmittionDisabled}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onSave();
                  }}
                >
                  <AddIcon />
                  <span className={classes.createNewTag}>Create new tag</span>
                </Button>
              </FlexBox>
            </MenuItem>
            {text.length >= MAX_TAG_LEN && (
              <MenuItem disabled className={classes.maxCountWarning}>
                <FlexBox gap={1} alignCenter pl={1} pr={1.75}>
                  <ErrorOutlineOutlinedIcon
                    style={{
                      fontSize: "16px",
                    }}
                    htmlColor={theme.palette.warning.main}
                  />
                  <WarningMaxCount />
                </FlexBox>
              </MenuItem>
            )}
          </div>
        )}
        renderOption={(props, option) => (
          <MenuItem {...props} className={classes.menuItem}>
            <Chip
              label={option}
              className={clsx(classes.chip, classes.autocompleteChip)}
              onClick={() => {
                onSave(option);
              }}
            />
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            className={classes.input}
            color="primary"
            variant="outlined"
            autoFocus
            onKeyUp={(event) => {
              if (event.code === enterKeyCode) {
                handleEnterKey();
              }
            }}
          />
        )}
        className={classes.inputRoot}
      />
    </FlexBox>
  );
}

export default Tags;
